import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pause, Play, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import DashboardAlertsCenter from "@/components/DashboardAlertsCenter";
import DashboardCashflowChart from "@/components/DashboardCashflowChart";
import DashboardMiniCharts from "@/components/DashboardMiniCharts";
import DashboardCalendarWidget from "@/components/DashboardCalendarWidget";

const SECTIONS = [
  { key: "alertas",       title: "Centro de alertas",        accent: "#DC2626", Component: DashboardAlertsCenter },
  { key: "pulso",         title: "Pulso financiero",         accent: "#0F8A5F", Component: DashboardCashflowChart },
  { key: "metricas",      title: "Métricas clave",           accent: "#E08A1F", Component: DashboardMiniCharts },
  { key: "calendario",    title: "Calendario y tareas",      accent: "#0E9488", Component: DashboardCalendarWidget },
];

const ROTATE_INTERVAL_MS = 15000;

/**
 * Modo presentación — overlay fullscreen que rota entre las 4 secciones
 * clave del dashboard cada 15s. Pensado para mostrar en pantalla grande
 * en oficina o comités. Navegación manual (←/→), pausa (espacio),
 * salir (Esc / botón X).
 */
export default function PresentationMode({ onClose }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const startedAtRef = useRef(Date.now());

  // Auto-rotación + barra de progreso
  useEffect(() => {
    if (paused) return;
    startedAtRef.current = Date.now();
    setProgress(0);
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      setProgress(Math.min(100, (elapsed / ROTATE_INTERVAL_MS) * 100));
    }, 100);
    const rotateTimer = setTimeout(() => {
      setIdx((i) => (i + 1) % SECTIONS.length);
    }, ROTATE_INTERVAL_MS);
    return () => {
      clearInterval(progressInterval);
      clearTimeout(rotateTimer);
    };
  }, [idx, paused]);

  // Atajos de teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % SECTIONS.length);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + SECTIONS.length) % SECTIONS.length);
      else if (e.key === " ") {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    // Solicitar pantalla completa
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = SECTIONS[idx];
  const Comp = current.Component;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[var(--legatum-bg)] text-[var(--legatum-text)] flex flex-col"
      data-testid="presentation-mode"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-10 py-4 border-b border-[var(--legatum-border)] bg-[var(--legatum-surface)]/60 backdrop-blur-md">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-9 w-9 rounded-md flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(135deg, var(--legatum-accent), var(--legatum-accent-2))" }}
          >
            <span className="font-bold tracking-wider" style={{ color: "var(--legatum-accent-fg)" }}>L</span>
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--legatum-text-muted)] font-bold">LEGATUM · MODO PRESENTACIÓN</div>
            <div className="text-base md:text-lg font-bold truncate" style={{ color: current.accent }}>
              {current.title}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Indicador secciones */}
          <div className="hidden md:flex items-center gap-1.5 mr-3" data-testid="presentation-dots">
            {SECTIONS.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setIdx(i)}
                title={s.title}
                data-testid={`presentation-dot-${i}`}
                className="h-2.5 w-8 rounded-full transition-colors"
                style={{
                  background: i === idx ? s.accent : "var(--legatum-elevated)",
                  border: `1px solid ${i === idx ? s.accent : "var(--legatum-border)"}`,
                }}
              />
            ))}
          </div>

          <button
            onClick={() => setIdx((i) => (i - 1 + SECTIONS.length) % SECTIONS.length)}
            data-testid="presentation-prev"
            className="h-9 w-9 rounded-md hover:bg-[var(--legatum-elevated)] flex items-center justify-center"
            title="Anterior (←)"
            aria-label="Sección anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPaused((p) => !p)}
            data-testid="presentation-pause"
            className="h-9 w-9 rounded-md hover:bg-[var(--legatum-elevated)] flex items-center justify-center"
            title={paused ? "Reanudar (Espacio)" : "Pausar (Espacio)"}
            aria-label={paused ? "Reanudar" : "Pausar"}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % SECTIONS.length)}
            data-testid="presentation-next"
            className="h-9 w-9 rounded-md hover:bg-[var(--legatum-elevated)] flex items-center justify-center"
            title="Siguiente (→)"
            aria-label="Sección siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            data-testid="presentation-close"
            className="h-9 w-9 rounded-md hover:bg-[#DC2626]/15 hover:text-[#DC2626] flex items-center justify-center"
            title="Salir (Esc)"
            aria-label="Salir del modo presentación"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="h-1 w-full bg-[var(--legatum-elevated)]">
        <div
          className="h-full transition-[width] duration-100 ease-linear"
          style={{
            width: paused ? `${progress}%` : `${progress}%`,
            background: paused ? "var(--legatum-text-muted)" : current.accent,
          }}
        />
      </div>

      {/* Slide */}
      <div className="flex-1 overflow-y-auto px-6 md:px-12 py-6 md:py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-[1600px] mx-auto"
            data-testid={`presentation-slide-${current.key}`}
            style={{ fontSize: "112%" }}
          >
            <Comp />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer hint */}
      <div className="px-6 md:px-10 py-2 border-t border-[var(--legatum-border)] flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[var(--legatum-text-muted)] font-bold">
        <div className="flex items-center gap-3">
          <span>{idx + 1} / {SECTIONS.length}</span>
          <span className="hidden md:inline">Rotación cada {ROTATE_INTERVAL_MS / 1000}s {paused && "· EN PAUSA"}</span>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <span>← → Navegar</span>
          <span>Espacio: pausar</span>
          <span>Esc: salir</span>
        </div>
      </div>
    </div>
  );
}

PresentationMode.Trigger = function Trigger({ onClick, className = "" }) {
  return (
    <button
      onClick={onClick}
      data-testid="presentation-mode-trigger"
      title="Modo presentación (pantalla completa con rotación automática)"
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--legatum-border)] bg-[var(--legatum-surface)] hover:border-[var(--legatum-accent)] hover:text-[var(--legatum-accent)] text-[var(--legatum-text-secondary)] text-xs font-bold uppercase tracking-widest transition-colors ${className}`}
    >
      <Maximize2 className="h-3.5 w-3.5" /> Modo presentación
    </button>
  );
};
